import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Search, Mail, Lock, User, Phone, ArrowLeft, Heart, Bell, Home } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SeekerLogin = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isArabic } = useLanguage();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    looking_for: 'rent',
    preferred_areas: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        // Login
        const response = await fetch(`${API_URL}/api/property-seekers/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            email: formData.email, 
            password: formData.password 
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          localStorage.setItem('seekerLoggedIn', 'true');
          localStorage.setItem('seekerId', data.id);
          localStorage.setItem('seekerName', data.name);
          localStorage.setItem('seekerEmail', data.email);
          toast({ title: isArabic ? 'تم تسجيل الدخول!' : 'Welcome back!' });
          navigate('/seeker/dashboard');
        } else {
          const error = await response.json();
          toast({ title: 'Error', description: error.detail || 'Invalid credentials', variant: 'destructive' });
        }
      } else {
        // Register
        const response = await fetch(`${API_URL}/api/property-seekers/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            password: formData.password,
            looking_for: formData.looking_for,
            preferred_areas: formData.preferred_areas.split(',').map(a => a.trim()).filter(a => a)
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          localStorage.setItem('seekerLoggedIn', 'true');
          localStorage.setItem('seekerId', data.id);
          localStorage.setItem('seekerName', data.name);
          localStorage.setItem('seekerEmail', data.email);
          toast({ title: isArabic ? 'تم إنشاء الحساب!' : 'Account Created!', description: 'Welcome to Harry & Jacobs' });
          navigate('/seeker/dashboard');
        } else {
          const error = await response.json();
          toast({ title: 'Error', description: error.detail || 'Registration failed', variant: 'destructive' });
        }
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Something went wrong', variant: 'destructive' });
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 pt-24 pb-12" dir={isArabic ? 'rtl' : 'ltr'}>
      <div className="max-w-md mx-auto px-4">
        <Link to="/signin" className="inline-flex items-center gap-2 text-gray-600 hover:text-green-600 mb-6">
          <ArrowLeft className={`w-4 h-4 ${isArabic ? 'rotate-180' : ''}`} />
          {isArabic ? 'العودة إلى تسجيل الدخول' : 'Back to Sign In'}
        </Link>

        <Card className="shadow-xl border-none">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-[#404040]">
                {isArabic ? 'بوابة الباحثين عن عقار' : 'Property Seeker Portal'}
              </h1>
              <p className="text-gray-600 mt-2">
                {isArabic 
                  ? 'احفظ المفضلات واحصل على تنبيهات' 
                  : 'Save favourites & get property alerts'}
              </p>
            </div>

            {/* Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1 mb-6">
              <button
                type="button"
                onClick={() => setIsLogin(true)}
                className={`flex-1 py-2 rounded-md text-sm font-medium transition-all ${
                  isLogin ? 'bg-white shadow text-green-600' : 'text-gray-600'
                }`}
              >
                {isArabic ? 'تسجيل الدخول' : 'Sign In'}
              </button>
              <button
                type="button"
                onClick={() => setIsLogin(false)}
                className={`flex-1 py-2 rounded-md text-sm font-medium transition-all ${
                  !isLogin ? 'bg-white shadow text-green-600' : 'text-gray-600'
                }`}
              >
                {isArabic ? 'حساب جديد' : 'Register'}
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <>
                  <div>
                    <Label htmlFor="name" className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" />
                      {isArabic ? 'الاسم الكامل' : 'Full Name'}
                    </Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder={isArabic ? 'اسمك الكامل' : 'Your full name'}
                      required={!isLogin}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-gray-400" />
                      {isArabic ? 'رقم الهاتف' : 'Phone Number'}
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="07700 123456"
                      className="mt-1"
                    />
                  </div>
                </>
              )}

              <div>
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gray-400" />
                  {isArabic ? 'البريد الإلكتروني' : 'Email'}
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="you@example.com"
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="password" className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-gray-400" />
                  {isArabic ? 'كلمة المرور' : 'Password'}
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="••••••••"
                  required
                  className="mt-1"
                />
              </div>

              {!isLogin && (
                <>
                  <div>
                    <Label htmlFor="looking_for" className="flex items-center gap-2">
                      <Home className="w-4 h-4 text-gray-400" />
                      {isArabic ? 'أبحث عن' : 'Looking to'}
                    </Label>
                    <select
                      id="looking_for"
                      value={formData.looking_for}
                      onChange={(e) => setFormData({...formData, looking_for: e.target.value})}
                      className="mt-1 w-full p-2 border rounded-md"
                    >
                      <option value="rent">{isArabic ? 'إيجار' : 'Rent'}</option>
                      <option value="buy">{isArabic ? 'شراء' : 'Buy'}</option>
                      <option value="both">{isArabic ? 'كلاهما' : 'Both'}</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="areas" className="flex items-center gap-2">
                      {isArabic ? 'المناطق المفضلة' : 'Preferred Areas'}
                    </Label>
                    <Input
                      id="areas"
                      value={formData.preferred_areas}
                      onChange={(e) => setFormData({...formData, preferred_areas: e.target.value})}
                      placeholder={isArabic ? 'مثال: هارو، ويمبلي' : 'e.g., Harrow, Wembley, Pinner'}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {isArabic ? 'افصل بين المناطق بفاصلة' : 'Separate areas with commas'}
                    </p>
                  </div>
                </>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-green-500 hover:bg-green-600 text-white py-6"
              >
                {loading ? (
                  <span className="animate-spin mr-2">⏳</span>
                ) : null}
                {isLogin 
                  ? (isArabic ? 'تسجيل الدخول' : 'Sign In')
                  : (isArabic ? 'إنشاء حساب' : 'Create Account')
                }
              </Button>
            </form>

            {/* Benefits */}
            <div className="mt-8 pt-6 border-t">
              <p className="text-sm text-gray-500 mb-3 text-center">
                {isArabic ? 'مميزات الحساب' : 'Account Benefits'}
              </p>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="p-2">
                  <Heart className="w-5 h-5 text-red-400 mx-auto mb-1" />
                  <span className="text-xs text-gray-600">{isArabic ? 'حفظ المفضلات' : 'Save Favourites'}</span>
                </div>
                <div className="p-2">
                  <Bell className="w-5 h-5 text-yellow-500 mx-auto mb-1" />
                  <span className="text-xs text-gray-600">{isArabic ? 'تنبيهات' : 'Alerts'}</span>
                </div>
                <div className="p-2">
                  <Search className="w-5 h-5 text-green-500 mx-auto mb-1" />
                  <span className="text-xs text-gray-600">{isArabic ? 'بحث مخصص' : 'Saved Searches'}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SeekerLogin;
